import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InputFieldWrapper } from '@components-shared/forms/FieldLabel';
import { KeyMapKeys } from '@locales/lokalise/keyMap';
import { Select } from 'antd';
import { RefSelectProps } from 'antd/lib/select';
import { INotificationWarning } from 'components/shared/shared.interfaces';
import { SelectSearchInput } from './selectSearchInput/selectSearchInput';
import styles from './styles.module.scss';
import { OptionComponent } from './types';

const { Option } = Select;

export type IProps = {
  name: string;
  title?: string;
  tooltip?: string;
  options: OptionComponent[];
  descriptionComponent: JSX.Element;
  notificationComponent: JSX.Element;
  onChange: (name: string, value: string) => void;
  required?: boolean;
  valid?: boolean;
  validationMessage?: string;
  className?: string;
  placeholder: string;
  getRequestData?: (value?: string) => Promise<void>;
  setOption?: (value: string) => void;
  selectedOptionValue: string;
  attributes: {
    disabled: boolean;
  };
  notificationsWarning: INotificationWarning;
};

export const SelectWithExtendedOptions = (props: IProps) => {
  const {
    name,
    title,
    options,
    descriptionComponent,
    notificationComponent,
    onChange,
    required,
    valid,
    validationMessage,
    className,
    placeholder,
    getRequestData,
    setOption,
    selectedOptionValue,
    attributes,
    tooltip,
  } = props;

  const { i18n } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [searchRequest, setSearchRequest] = useState<string>();
  const selectRef = useRef<RefSelectProps>(null);
  const isSelectDisabled = attributes.disabled;

  const onChangeSelect = (option: string) => {
    onChange(name, option);
  };

  const getOptionsData = useCallback(
    async (value?: string) => {
      if (getRequestData) {
        setLoading(true);

        await getRequestData(value);

        setLoading(false);
      }
    },
    [getRequestData],
  );

  const onSelect = (value: string) => {
    if (setOption) {
      setOption(value);
    }
  };

  const getOption = (optionContent: OptionComponent, index: number) => {
    return (
      <Option key={index} value={optionContent.optionValue}>
        {optionContent.optionComponent}
      </Option>
    );
  };

  const setFocusSelector = () => selectRef.current?.focus();

  return (
    <InputFieldWrapper
      name={name}
      title={title}
      tooltip={tooltip}
      valid={valid}
      validationMessage={validationMessage}
      required={required}
      className={className}>
      <div className={`form__select-wrapper ${styles.select} `}>
        <Select
          className={`${styles.select} form__input `}
          onChange={onChangeSelect}
          suffixIcon={null}
          placeholder={i18n.t(placeholder as KeyMapKeys)}
          popupMatchSelectWidth={true}
          defaultValue={selectedOptionValue}
          ref={selectRef}
          virtual={false}
          onSelect={(optionValue) => onSelect(optionValue)}
          getPopupContainer={(trigger) => trigger.parentNode}
          popupClassName={styles.select__dropdownWrapper}
          disabled={isSelectDisabled}
          dropdownRender={(menu) => {
            return (
              <>
                <SelectSearchInput
                  searchRequest={searchRequest}
                  setSearchRequest={setSearchRequest}
                  loading={loading}
                  getOptionsData={getOptionsData}
                  setFocusSelector={setFocusSelector}
                />
                {menu}
              </>
            );
          }}>
          {options.map((option, index) => getOption(option, index))}
        </Select>
      </div>
      {descriptionComponent}
      {notificationComponent}
    </InputFieldWrapper>
  );
};
